import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  REACTION_EMOJIS,
  acknowledgeMessages,
  clearTyping,
  deleteConversationForEveryone,
  deleteConversationForMe,
  editMessage,
  isConversationVisible,
  isOtherTyping,
  markConversationUnread,
  messageReceipt,
  myReaction,
  sendMessage,
  setTyping,
  softDeleteMessage,
  toggleReaction,
  undoDeleteConversationForEveryone,
  undoDeleteConversationForMe,
} from '../lib/chat';
import { formatPrice, relativeTime } from '../lib/format';
import Empty from './Empty';
import Icon from './Icon';

function messageDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (typeof ts === 'number') return new Date(ts);
  return null;
}

function dayKey(ts) {
  const d = messageDate(ts);
  if (!d) return '';
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(ts) {
  const d = messageDate(ts);
  if (!d) return '';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startMsg = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startToday - startMsg) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function messageTime(ts) {
  const d = messageDate(ts);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function replySenderLabel(reply, uid) {
  if (!reply) return '';
  return reply.senderId === uid ? 'You' : 'Seller';
}

export default function MessagesSheetContent({
  user,
  threadId,
  onSelectThread,
  onOpenListing,
}) {
  const [convos, setConvos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [inboxMenu, setInboxMenu] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [typingTick, setTypingTick] = useState(0);
  const [undo, setUndo] = useState(null);
  const [inboxError, setInboxError] = useState('');
  const streamRef = useRef(null);
  const undoTimer = useRef(null);
  const inputRef = useRef(null);
  const highlightTimer = useRef(null);
  const typingTimer = useRef(null);
  const typingSentRef = useRef(false);

  const closeMenus = () => {
    setInboxMenu(null);
    setMessageMenu(null);
  };

  const menuAnchorStyle = (rect, estimatedHeight = 236) => {
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const right = Math.max(8, window.innerWidth - rect.right);
    return {
      openUp,
      style: openUp
        ? { right, bottom: window.innerHeight - rect.top + gap }
        : { right, top: rect.bottom + gap },
    };
  };

  const openInboxMenu = (event, convo) => {
    event.stopPropagation();
    setMessageMenu(null);
    if (inboxMenu?.convo?.id === convo.id) {
      setInboxMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const { openUp, style } = menuAnchorStyle(rect, 236);
    setInboxMenu({ convo, openUp, style });
  };

  const openMessageMenu = (event, message, mine) => {
    event.stopPropagation();
    setInboxMenu(null);
    if (messageMenu?.message?.id === message.id) {
      setMessageMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = mine ? 280 : 220;
    const { openUp, style } = menuAnchorStyle(rect, estimatedHeight);
    // Prefer anchoring near the button; for own bubbles keep right-aligned
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 196));
    const anchored = mine
      ? style
      : openUp
        ? { left, bottom: window.innerHeight - rect.top + 6 }
        : { left, top: rect.bottom + 6 };
    setMessageMenu({ message, mine, openUp, style: anchored });
  };

  useEffect(() => {
    if (!inboxMenu && !messageMenu) return undefined;
    const onRepositionClose = () => {
      setInboxMenu(null);
      setMessageMenu(null);
    };
    window.addEventListener('resize', onRepositionClose);
    document.addEventListener('scroll', onRepositionClose, true);
    return () => {
      window.removeEventListener('resize', onRepositionClose);
      document.removeEventListener('scroll', onRepositionClose, true);
    };
  }, [inboxMenu, messageMenu]);

  useEffect(() => {
    const id = window.setInterval(() => setTypingTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let unsub = () => {};
    let cancelled = false;
    (async () => {
      try {
        await auth.authStateReady();
      } catch {
        /* ignore */
      }
      if (cancelled || !auth.currentUser?.uid) return;
      const uid = auth.currentUser.uid;
      const q = query(
        collection(db, 'conversations'),
        where('participantIds', 'array-contains', uid),
        orderBy('updatedAt', 'desc'),
      );
      unsub = onSnapshot(
        q,
        (s) => {
          const all = s.docs.map((d) => ({ id: d.id, ...d.data() }));
          setConvos(all.filter((c) => isConversationVisible(c, uid)));
          setInboxError('');
        },
        (err) => {
          console.error('[Sosello] conversations failed', err);
          setConvos([]);
          setInboxError(
            err?.code === 'permission-denied'
              ? 'Couldn’t load conversations (permission denied). Try signing out and back in.'
              : err?.message || 'Couldn’t load conversations.',
          );
        },
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setEditing(null);
      setReplyingTo(null);
      return undefined;
    }
    return onSnapshot(
      query(collection(db, 'conversations', threadId, 'messages'), orderBy('createdAt', 'asc')),
      (s) => {
        const next = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(next);
        acknowledgeMessages(threadId, next, user.uid, true).catch(() => {});
      },
      (err) => console.error('[Sosello] messages failed', err),
    );
  }, [threadId, user.uid]);

  useEffect(() => {
    if (!threadId) return undefined;
    return () => {
      clearTyping(threadId, user.uid).catch(() => {});
    };
  }, [threadId, user.uid]);

  useEffect(() => {
    if (!streamRef.current) return;
    streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [messages, threadId]);

  useEffect(() => () => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
  }, []);

  const active = useMemo(
    () => convos.find((x) => x.id === threadId),
    [convos, threadId],
  );

  const otherUid = useMemo(() => {
    if (!active?.participantIds || !user?.uid) return '';
    return active.participantIds.find((id) => id !== user.uid) || '';
  }, [active, user?.uid]);

  const showTyping = useMemo(
    () => isOtherTyping(active, otherUid),
    [active, otherUid, messages.length],
  );

  const unreadTotal = useMemo(
    () => convos.filter((c) => unreadFor(c, user.uid)).length,
    [convos, user.uid],
  );

  const showUndo = (payload) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo(payload);
    undoTimer.current = setTimeout(() => setUndo(null), 6000);
  };

  const cancelEdit = () => {
    setEditing(null);
    setText('');
  };

  const cancelReply = () => setReplyingTo(null);

  const startReply = (m) => {
    if (m.deleted) return;
    setReplyingTo(m);
    setEditing(null);
    setMessageMenu(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const jumpToMessage = (messageId) => {
    if (!messageId || !streamRef.current) return;
    const el = streamRef.current.querySelector(`[data-message-id="${CSS.escape(messageId)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightedMessageId(null), 1100);
  };

  const pulseTyping = () => {
    if (!threadId) return;
    if (!typingSentRef.current) {
      typingSentRef.current = true;
      setTyping(threadId, user.uid, true).catch(() => {});
    }
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      typingSentRef.current = false;
      clearTyping(threadId, user.uid).catch(() => {});
    }, 2000);
  };

  const startEdit = (m) => {
    setEditing(m);
    setReplyingTo(null);
    setText(m.text || '');
    setMessageMenu(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const copyMessage = async (m) => {
    if (!m?.text) return;
    try {
      await navigator.clipboard.writeText(m.text);
    } catch {
      /* ignore */
    }
    setMessageMenu(null);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !threadId || sending) return;
    const body = text.trim();
    setSending(true);
    try {
      if (editing) {
        await editMessage(threadId, editing.id, body, user.uid);
        setEditing(null);
        setText('');
      } else {
        const replyPayload = replyingTo
          ? {
              messageId: replyingTo.id,
              text: replyingTo.deleted ? 'Message deleted' : (replyingTo.text || ''),
              senderId: replyingTo.senderId,
            }
          : null;
        setText('');
        setReplyingTo(null);
        await sendMessage(threadId, body, user.uid, replyPayload);
      }
      typingSentRef.current = false;
      await clearTyping(threadId, user.uid).catch(() => {});
    } catch (err) {
      console.error('[Sosello] send/edit failed', err);
      if (!editing) setText(body);
    } finally {
      setSending(false);
    }
  };

  const onMarkUnread = async (c) => {
    closeMenus();
    try {
      await markConversationUnread(c.id, user.uid);
    } catch (err) {
      console.error('[Sosello] mark unread failed', err);
    }
  };

  const onDeleteForMe = async (c) => {
    closeMenus();
    if (threadId === c.id) onSelectThread?.(null);
    try {
      await deleteConversationForMe(c.id, user.uid);
      showUndo({
        label: 'Conversation deleted',
        undo: () => undoDeleteConversationForMe(c.id, user.uid),
      });
    } catch (err) {
      console.error('[Sosello] delete for me failed', err);
    }
  };

  const onDeleteForEveryone = async (c) => {
    closeMenus();
    if (threadId === c.id) onSelectThread?.(null);
    try {
      await deleteConversationForEveryone(c.id);
      showUndo({
        label: 'Conversation deleted',
        undo: () => undoDeleteConversationForEveryone(c.id),
      });
    } catch (err) {
      console.error('[Sosello] delete for everyone failed', err);
    }
  };

  const onDeleteMessage = async (m) => {
    setMessageMenu(null);
    if (!window.confirm('Delete this message? It will be removed for everyone in the chat.')) {
      return;
    }
    if (editing?.id === m.id) cancelEdit();
    if (replyingTo?.id === m.id) cancelReply();
    try {
      await softDeleteMessage(threadId, m.id);
    } catch (err) {
      console.error('[Sosello] delete message failed', err);
    }
  };

  const onReact = async (m, emoji) => {
    // Keep picker open feel: close after choice like WhatsApp.
    setMessageMenu(null);
    try {
      await toggleReaction(threadId, m.id, emoji, user.uid);
    } catch (err) {
      console.error('[Sosello] reaction failed', err);
    }
  };

  const reactionEntries = (m) =>
    Object.entries(m.reactions || {}).filter(([, users]) => Array.isArray(users) && users.length);

  const otherParticipant = (c) =>
    (c.participantIds || []).find((id) => id && id !== user.uid) || '';

  // typingTick keeps inbox typing labels fresh as pulses expire.
  void typingTick;

  return (
    <div className={`messages-shell in-sheet ${threadId ? 'thread-open' : ''} ${inboxMenu || messageMenu ? 'has-open-menu' : ''}`}>
      {(inboxMenu || messageMenu) && (
        <button type="button" className="menu-dismiss" aria-label="Close menu" onClick={closeMenus} />
      )}

      <aside className="inbox">
        <div className="inbox-toolbar">
          <span className="inbox-toolbar-label">
            {convos.length
              ? `${convos.length} conversation${convos.length === 1 ? '' : 's'}`
              : 'Inbox'}
          </span>
          {unreadTotal > 0 ? (
            <span className="inbox-toolbar-badge">{unreadTotal} unread</span>
          ) : null}
        </div>

        <div className="inbox-list">
          {inboxError ? (
            <div className="inbox-empty">
              <Empty icon="message" title="Messages unavailable" body={inboxError} />
            </div>
          ) : convos.length ? (
            convos.map((c) => {
              const unread = unreadFor(c, user.uid);
              const activeRow = threadId === c.id;
              const menuOpen = inboxMenu?.convo?.id === c.id;
              const typing = isOtherTyping(c, otherParticipant(c));
              return (
                <div
                  key={c.id}
                  className={`inbox-row-wrap ${activeRow ? 'active' : ''} ${unread ? 'unread' : ''} ${menuOpen ? 'is-menu-open' : ''}`}
                >
                  <button
                    type="button"
                    className={`inbox-row ${activeRow ? 'active' : ''} ${unread ? 'unread' : ''}`}
                    onClick={() => onSelectThread?.(c.id)}
                  >
                    <div className="inbox-photo">
                      {c.listingPhoto ? (
                        <img src={c.listingPhoto} alt="" />
                      ) : (
                        <Icon name="message" size={18} />
                      )}
                      {unread ? <span className="inbox-photo-dot" /> : null}
                    </div>
                    <div className="inbox-copy">
                      <div className="inbox-top">
                        <b>{c.listingTitle || 'Conversation'}</b>
                        <time>{relativeTime(c.updatedAt)}</time>
                      </div>
                      {typing ? (
                        <small className="inbox-typing">Typing...</small>
                      ) : (
                        <small className={unread ? 'strong' : ''}>
                          {c.lastMessage || 'Start the conversation'}
                        </small>
                      )}
                    </div>
                  </button>

                  <div className="menu-wrap inbox-menu">
                    <button
                      type="button"
                      className="icon-button inbox-menu-btn"
                      aria-label="Conversation options"
                      aria-expanded={menuOpen}
                      onClick={(e) => openInboxMenu(e, c)}
                    >
                      <Icon name="more" size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="inbox-empty">
              <Empty
                icon="message"
                title="No messages yet"
                body="Open a listing and tap Message seller to start chatting."
              />
            </div>
          )}
        </div>
      </aside>

      <div className="thread-pane">
        {active ? (
          <>
            <header className="thread-bar">
              <button
                type="button"
                className="icon-button mobile-only-back"
                onClick={() => onSelectThread?.(null)}
                aria-label="Back to inbox"
              >
                <Icon name="chevronLeft" />
              </button>
              <button
                type="button"
                className="thread-listing"
                onClick={() => active.listingId && onOpenListing?.(active.listingId)}
              >
                <div className="inbox-photo sm">
                  {active.listingPhoto ? (
                    <img src={active.listingPhoto} alt="" />
                  ) : (
                    <Icon name="image" size={16} />
                  )}
                </div>
                <div className="thread-listing-copy">
                  <b>{active.listingTitle || 'Listing'}</b>
                  <small>
                    {formatPrice({
                      price: active.listingPrice,
                      isFree: active.listingPrice === 0,
                    })}
                    <span className="thread-listing-sep">·</span>
                    View listing
                  </small>
                </div>
                <Icon name="chevron" size={16} className="thread-listing-chevron" />
              </button>
            </header>

            <div className="thread-stream" ref={streamRef}>
              {messages.length ? (
                messages.map((m, i) => {
                  const mine = m.senderId === user.uid;
                  const prev = messages[i - 1];
                  const showDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
                  const reactions = reactionEntries(m);
                  const receipt = messageReceipt(m, user.uid, otherUid);
                  return (
                    <div
                      key={m.id}
                      className={`thread-item ${mine ? 'mine' : ''} ${highlightedMessageId === m.id ? 'is-highlighted' : ''}`}
                      data-message-id={m.id}
                    >
                      {showDay && (
                        <div className="thread-day">{dayLabel(m.createdAt)}</div>
                      )}
                      <div
                        className={`bubble-row ${mine ? 'mine' : ''} swipe-reply-row`}
                        onTouchStart={(e) => {
                          if (m.deleted) return;
                          const t = e.changedTouches[0];
                          e.currentTarget.dataset.touchX = String(t?.clientX ?? 0);
                          e.currentTarget.dataset.touchY = String(t?.clientY ?? 0);
                          e.currentTarget.dataset.swiping = '0';
                        }}
                        onTouchMove={(e) => {
                          if (m.deleted) return;
                          const row = e.currentTarget;
                          const startX = Number(row.dataset.touchX || 0);
                          const startY = Number(row.dataset.touchY || 0);
                          const t = e.changedTouches[0];
                          const dx = (t?.clientX ?? 0) - startX;
                          const dy = (t?.clientY ?? 0) - startY;
                          if (row.dataset.swiping !== '1') {
                            if (Math.abs(dx) < 10) return;
                            if (Math.abs(dx) <= Math.abs(dy) * 1.2) return;
                            row.dataset.swiping = '1';
                            row.classList.add('is-swiping');
                          }
                          const max = 72;
                          const raw = Math.max(0, dx);
                          const capped = raw <= max ? raw : max + (raw - max) * 0.15;
                          const bubble = row.querySelector('.bubble');
                          const icon = row.querySelector('.swipe-reply-icon');
                          if (bubble) bubble.style.transform = `translateX(${capped}px)`;
                          if (icon) {
                            const progress = Math.min(capped / 52, 1);
                            icon.style.opacity = String(progress);
                            icon.style.transform = `translateY(-50%) scale(${0.75 + 0.25 * progress})`;
                          }
                          row.dataset.swipeX = String(capped);
                        }}
                        onTouchEnd={(e) => {
                          const row = e.currentTarget;
                          const capped = Number(row.dataset.swipeX || 0);
                          const bubble = row.querySelector('.bubble');
                          const icon = row.querySelector('.swipe-reply-icon');
                          if (capped >= 52 && !m.deleted) startReply(m);
                          if (bubble) {
                            bubble.style.transition = 'transform 0.22s ease';
                            bubble.style.transform = 'translateX(0)';
                            window.setTimeout(() => {
                              if (bubble) bubble.style.transition = '';
                            }, 240);
                          }
                          if (icon) {
                            icon.style.opacity = '';
                            icon.style.transform = '';
                          }
                          row.classList.remove('is-swiping');
                          row.dataset.swipeX = '0';
                          row.dataset.swiping = '0';
                        }}
                        onTouchCancel={(e) => {
                          const row = e.currentTarget;
                          const bubble = row.querySelector('.bubble');
                          const icon = row.querySelector('.swipe-reply-icon');
                          if (bubble) bubble.style.transform = '';
                          if (icon) {
                            icon.style.opacity = '';
                            icon.style.transform = '';
                          }
                          row.classList.remove('is-swiping');
                          row.dataset.swipeX = '0';
                          row.dataset.swiping = '0';
                        }}
                      >
                        {!m.deleted ? (
                          <div className="swipe-reply-icon" aria-hidden="true">
                            <Icon name="reply" size={16} />
                          </div>
                        ) : null}
                        <div className={`bubble ${mine ? 'mine' : ''} ${m.deleted ? 'deleted' : ''}`}>
                          {m.replyTo?.messageId ? (
                            <button
                              type="button"
                              className="bubble-reply"
                              onClick={() => jumpToMessage(m.replyTo.messageId)}
                            >
                              <span className="bubble-reply-label">
                                {replySenderLabel(m.replyTo, user.uid)}
                              </span>
                              <span className="bubble-reply-text">
                                {m.replyTo.text || 'Message'}
                              </span>
                            </button>
                          ) : null}
                          {m.deleted ? <i>Message deleted</i> : m.text}
                          <div className="bubble-meta">
                            {m.edited && !m.deleted ? (
                              <span className="edited-label">edited</span>
                            ) : null}
                            <time>{messageTime(m.createdAt)}</time>
                            {mine && !m.deleted && receipt ? (
                              <span className={`bubble-receipt ${receipt}`} aria-hidden="true">
                                {receipt === 'read' ? '✓✓' : receipt === 'delivered' ? '✓✓' : '✓'}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {!m.deleted && (
                          <div
                            className={`bubble-actions ${messageMenu?.message?.id === m.id ? 'is-open' : ''}`}
                          >
                            <button
                              type="button"
                              className="icon-button bubble-action-btn"
                              aria-label="Reply"
                              onClick={() => startReply(m)}
                            >
                              <Icon name="reply" size={14} />
                            </button>
                            <div className="menu-wrap bubble-menu">
                              <button
                                type="button"
                                className="icon-button bubble-menu-btn"
                                aria-label="Message options"
                                aria-expanded={messageMenu?.message?.id === m.id}
                                onClick={(e) => openMessageMenu(e, m, mine)}
                              >
                                <Icon name="more" size={14} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {reactions.length > 0 && (
                        <div className={`reaction-chips ${mine ? 'mine' : ''}`}>
                          {reactions.map(([emoji, users]) => (
                            <button
                              key={emoji}
                              type="button"
                              className={`reaction-chip ${users.includes(user.uid) ? 'active' : ''}`}
                              onClick={() => onReact(m, emoji)}
                            >
                              {emoji} {users.length > 1 ? users.length : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="thread-empty">
                  <div className="empty-icon">
                    <Icon name="message" size={22} />
                  </div>
                  <p>Say hello and ask about pickup or condition.</p>
                </div>
              )}
            </div>

            {showTyping ? (
              <div className="thread-typing" aria-live="polite">
                <span className="typing-dots" aria-hidden="true">
                  <span /><span /><span />
                </span>
              </div>
            ) : null}

            <div className="thread-composer-wrap">
              {replyingTo ? (
                <div className="composer-reply-banner">
                  <div>
                    <b>Replying to {replySenderLabel(replyingTo, user.uid)}</b>
                    <span>{replyingTo.deleted ? 'Message deleted' : replyingTo.text}</span>
                  </div>
                  <button type="button" className="text-button" onClick={cancelReply} aria-label="Cancel reply">
                    <Icon name="x" size={16} />
                  </button>
                </div>
              ) : null}
              {editing ? (
                <div className="composer-edit-banner">
                  <div>
                    <b>Editing message</b>
                    <span>Make your changes, then save</span>
                  </div>
                  <button type="button" className="text-button" onClick={cancelEdit}>
                    Cancel
                  </button>
                </div>
              ) : null}
              <form className="thread-composer" onSubmit={send}>
                <input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    pulseTyping();
                  }}
                  placeholder={editing ? 'Edit message…' : replyingTo ? 'Reply…' : 'Write a message…'}
                  autoComplete="off"
                />
                <button
                  type="submit"
                  disabled={!text.trim() || sending}
                  aria-label={editing ? 'Save' : 'Send'}
                >
                  <Icon name="send" size={18} />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="thread-placeholder">
            <div className="thread-placeholder-card">
              <div className="empty-icon">
                <Icon name="message" size={28} />
              </div>
              <h2>Pick a conversation</h2>
              <p>Select someone from your inbox to keep the chat going.</p>
            </div>
          </div>
        )}
      </div>

      {inboxMenu ? (
        <div
          className={`menu-pop menu-pop--overlay ${inboxMenu.openUp ? 'opens-up' : 'opens-down'}`}
          style={inboxMenu.style}
          role="menu"
        >
          <button type="button" onClick={() => onMarkUnread(inboxMenu.convo)}>
            <Icon name="eye" size={16} /> Mark as unread
          </button>
          {inboxMenu.convo.listingId ? (
            <button
              type="button"
              onClick={() => {
                closeMenus();
                onOpenListing?.(inboxMenu.convo.listingId);
              }}
            >
              <Icon name="tag" size={16} /> View listing
            </button>
          ) : null}
          <button type="button" className="danger" onClick={() => onDeleteForMe(inboxMenu.convo)}>
            <Icon name="trash" size={16} /> Delete for me
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => onDeleteForEveryone(inboxMenu.convo)}
          >
            <Icon name="trash" size={16} /> Delete for everyone
          </button>
        </div>
      ) : null}

      {messageMenu ? (
        <div
          className={`menu-pop menu-pop--overlay message-menu-pop ${messageMenu.openUp ? 'opens-up' : 'opens-down'}`}
          style={messageMenu.style}
          role="menu"
        >
          <div className="reaction-picker">
            {REACTION_EMOJIS.map((emoji) => {
              const active = myReaction(messageMenu.message, user.uid) === emoji;
              return (
                <button
                  key={emoji}
                  type="button"
                  className={`reaction-pick ${active ? 'is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => onReact(messageMenu.message, emoji)}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => startReply(messageMenu.message)}>
            <Icon name="reply" size={16} /> Reply
          </button>
          <button type="button" onClick={() => copyMessage(messageMenu.message)}>
            <Icon name="copy" size={16} /> Copy
          </button>
          {messageMenu.mine && (
            <>
              <button type="button" onClick={() => startEdit(messageMenu.message)}>
                <Icon name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => onDeleteMessage(messageMenu.message)}
              >
                <Icon name="trash" size={16} /> Delete
              </button>
            </>
          )}
        </div>
      ) : null}

      {undo && (
        <div className="chat-undo-bar">
          <span>{undo.label}</span>
          <button
            type="button"
            onClick={async () => {
              try {
                await undo.undo();
              } catch (err) {
                console.error('[Sosello] undo failed', err);
              }
              setUndo(null);
            }}
          >
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

function unreadFor(c, uid) {
  const count = c.unreadCounts?.[uid] || 0;
  const marked = Array.isArray(c.markedUnreadFor) && c.markedUnreadFor.includes(uid);
  return count > 0 || marked;
}
